import { getOptionListApi } from "features/mst_option/api/optionApi";
import {
  MstItemOptionListsByMode,
  MstItemOptionSelectRequest,
  OptionItem,
} from "features/mst_option/types/option";

const DISABLED = "なし" as const;

/** Car context from session (車種を選択) — runtime `selectedTire` may include mmm_hole / mmm_pitch from API. */
export type CarOptionSession = {
  brnd_label?: string;
  selectedTire?: unknown;
};

export function getCarOptionParamsFromSession(car: CarOptionSession | null | undefined): Pick<
  MstItemOptionSelectRequest,
  "brnd_label" | "mmm_hole" | "mmm_pitch"
> {
  const st = car?.selectedTire as
    | { mmm_hole?: number | string; mmm_pitch?: string }
    | undefined;
  const brnd = car?.brnd_label?.trim();
  const hasHole =
    st &&
    st.mmm_hole !== undefined &&
    st.mmm_hole !== null &&
    !(typeof st.mmm_hole === "string" && st.mmm_hole === "");
  const hasPitch = Boolean(st?.mmm_pitch);
  if (!brnd && !hasHole && !hasPitch) {
    return {};
  }
  return {
    ...(brnd ? { brnd_label: brnd } : {}),
    ...(hasHole ? { mmm_hole: st!.mmm_hole } : {}),
    ...(hasPitch ? { mmm_pitch: st!.mmm_pitch } : {}),
  };
}

export function findOptionItemByValue(list: OptionItem[], selected: string): OptionItem | undefined {
  if (!selected || selected === DISABLED) return undefined;
  const numericId = Number(selected);
  if (!Number.isNaN(numericId)) {
    const byId = list.find((el) => el.id === numericId);
    if (byId) return byId;
  }
  return list.find((el) => el.option_cd === selected);
}

export function resolveLockNutNum(
  selectedLockNut: string,
  lockNatList: OptionItem[],
  lockNutFlag: boolean,
): number | undefined {
  if (!lockNutFlag || !selectedLockNut || selectedLockNut === DISABLED) return undefined;
  const item = findOptionItemByValue(lockNatList, selectedLockNut);
  return item?.nut_num;
}

export type FastenerOptionInput = {
  lock_nut_flag: boolean;
  lock_nut: string;
};

/**
 * ナット一覧のみ（既存のロックナット一覧で `lock_nut_num` を解決 — 全モード再取得しない）
 */
export async function fetchNatMstItemOptions(
  car: unknown,
  fasteners: FastenerOptionInput,
  lockNatList: OptionItem[],
): Promise<OptionItem[]> {
  const carParams = getCarOptionParamsFromSession(car as CarOptionSession | null | undefined);
  const lockNatFlag: 0 | 1 =
    fasteners.lock_nut_flag &&
      Boolean(fasteners.lock_nut) &&
      fasteners.lock_nut !== DISABLED
      ? 1
      : 0;
  const lockNutNum = resolveLockNutNum(fasteners.lock_nut, lockNatList, fasteners.lock_nut_flag);

  const natBody: MstItemOptionSelectRequest = {
    mode: "nat",
    ...carParams,
    lock_nat_flag: lockNatFlag,
    ...(lockNatFlag === 1 && lockNutNum !== undefined ? { lock_nut_num: lockNutNum } : {}),
  };

  const natRes = await getOptionListApi(natBody);
  return natRes.data ?? [];
}

/**
 * Loads all four option groups: locknat first (for `nut_num`), then nat with lock coupling.
 */
export async function fetchAllMstItemOptions(
  car: unknown,
  fasteners: FastenerOptionInput,
): Promise<MstItemOptionListsByMode> {
  const carParams = getCarOptionParamsFromSession(car as CarOptionSession | null | undefined);
  const lockNatParams: MstItemOptionSelectRequest = {
    mode: "locknat",
    ...("brnd_label" in carParams ? { brnd_label: carParams.brnd_label } : {}),
    ...("mmm_pitch" in carParams ? { mmm_pitch: carParams.mmm_pitch } : {}),
  };

  const [hubRes, wcoatingRes, locknatRes] = await Promise.all([
    getOptionListApi({ mode: "hub" }),
    getOptionListApi({ mode: "wcoating" }),
    getOptionListApi(lockNatParams),
  ]);

  const locknat = locknatRes.data ?? [];
  const lockNatFlag: 0 | 1 =
    fasteners.lock_nut_flag &&
      Boolean(fasteners.lock_nut) &&
      fasteners.lock_nut !== DISABLED
      ? 1
      : 0;
  const lockNutNum = resolveLockNutNum(fasteners.lock_nut, locknat, fasteners.lock_nut_flag);

  const natBody: MstItemOptionSelectRequest = {
    mode: "nat",
    ...carParams,
    lock_nat_flag: lockNatFlag,
    ...(lockNatFlag === 1 && lockNutNum !== undefined ? { lock_nut_num: lockNutNum } : {}),
  };

  const natRes = await getOptionListApi(natBody);

  return {
    nat: natRes.data ?? [],
    locknat,
    hub: hubRes.data ?? [],
    wcoating: wcoatingRes.data ?? [],
  };
}
